import React, { useEffect, useState } from 'react';
import { currentCustomer, duckify, filterDuck } from 'utils/duckify';
import { modals, schoolYears as schoolYearsDuck } from 'ducks';
import { formatCurrency } from 'utils/i18n';
import Placeholder from '../Placeholder';
import './styles/RevenueRecognitionReport.scss';
import {compose} from "redux";
import { Link, withRouter } from 'react-router';
import withFiltering from "hocs/withFiltering";
import ContactList from '../ContactList/ContactList';
import queryString from 'query-string';
import history from '../../shared_history';
import classNames from 'classnames';
import { ContactRow } from '../ContactList/ContactRows';
import SchoolYearSelect from '../SchoolYearSelect';
import { getMetaContent } from '../../utils';
import { pollJob } from '../../utils/jobPoller';
import ReportExportButton from './ReportExportButton';
import ReportHeader from './ReportHeader';
import ReportRunButtons from './ReportRunButtons';

const SchoolYearFilter = ({location, schoolYears, schoolYearId, disabled}) => {
  const query = queryString.parse(location.search);

  const updateUrl = (year) => {
    const { job_id, ...rest } = queryString.parse(location.search); // Exclude job_id
    history.push({
      ...location,
      search: `?${queryString.stringify({
        ...rest,
        school_year: year
      })}`
    });
  }

  if (!schoolYearId && query.school_year) {
    schoolYearId = query.school_year;
  }

  useEffect(() => {
    if (!query.job_id && !query.school_year && !schoolYearId && schoolYears.length > 0) {
      let defaultYear = schoolYears.find(schoolYear => schoolYear.default_for_reporting) || schoolYears[schoolYears.length - 1];
      updateUrl(defaultYear.id);
    }
  }, [schoolYears, schoolYearId]);

  return (
    <div>
      <SchoolYearSelect
        onSelect={(year) => updateUrl(year)}
        schoolYears={schoolYears}
        schoolYearId={schoolYearId}
        disabled={disabled}
      />
    </div>
  )
};

const RevenueRecognitionReport = ({
  schoolYears,
  schoolYearId,
  dispatch,
  allDucksReady
}) => {
  const [currentJobId, setCurrentJobId] = useState('');
  const [ready, setReady] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [currentSchoolYearId, setCurrentSchoolYearId] = useState(schoolYearId);
  const [report, setReport] = useState({});
  const [columns, setColumns] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [newSchoolYearId, setNewSchoolYearId] = useState(date);

  const {
    date, rows = [], count, totals, generated_at
  } = report;

  const constructReport = (json, search) => {
    const rows = json.rows
    setReportRows(rows);
  }

  const updateUrl = (year) => {
    const query = queryString.parse(location.search);
    history.push({
      ...location,
      search: `?${queryString.stringify({
        ...query,
        school_year: year
      })}`
    });
  }

  // Queries report API when URL parameters change
  useEffect(
    () => {
      if (schoolYearId === undefined && schoolYears[0] !== undefined) {
        setCurrentSchoolYearId(schoolYears[0].id);
      }
      setReady(false);
      setReportLoading(false);
      let search = queryString.parse(location.search);
      if (search.job_id) {
        setReady(true);
        setReportLoading(true);
        if (search.job_id !== currentJobId) {

          fetch(`/api/revenue_recognition_report/${search.job_id}`, { credentials: 'include' })
            .then(response => {
              if (!response.ok) {
                throw Error(response.statusText);
              }
              return response.json();
            })
            .then(json => {
              updateColumns(json)
              constructReport(json, search);
              setReady(true);
              setReportLoading(false);
              updateUrl(json.school_year_id);
            })
            .catch(() => {
              dispatch(modals.actions.show('UnexpectedError'));
              setReady(false);
              setReportLoading(false);
            });
        }
      }
    },
    [location.search, currentSchoolYearId, allDucksReady]
  );

  const updateColumns = (json) => {
    let newColumns = [{
      header: 'Fees',
      value: (row) =>
        <td className={'revenue-recognition-report-billed-category-col'}>
          { row.billing_category_name || 'Total'}
        </td>
      },
      {
        header: 'GL ID',
        value: (row) =>
          <td className={'revenue-recognition-report-billed-account-col'}>
            { row.billing_category_account || '' }
          </td>
      }]

    for (let i = 0; i < json.column_headers.length; i++) {
      newColumns.push({
        header: json.column_headers[i].label,
        value: (row) => <td className={'contact-list-react-type-currency'}>{formatCurrency((!!row.monthly_amounts && row.monthly_amounts[json.column_headers[i].key] && row.monthly_amounts[json.column_headers[i].key].billed_amount) || 0)}</td>,
      });
    }

    newColumns.push({
      header: 'Total',
      value: 'billed_amount',
      type: 'currency',
      columnClassNames: classNames('revenue-recognition-report-billed-total-col')
    })

    setColumns(newColumns);
  }

  const getNewReport = (force = false) => {
    if (!ready || newSchoolYearId !== schoolYearId || force) {
      const query = queryString.parse(location.search);
      const schoolYearForReport = query.school_year;
      const searchParams = { school_year: schoolYearForReport };
      const reqSearchString = `?${queryString.stringify(searchParams)}`;

      setReportLoading(true);
      setReady(true);

      const token = getMetaContent('csrf-token');

      fetch(`/api/revenue_recognition_report${reqSearchString}&force=${force}`, {
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', 'X-CSRF-Token': token},
        method: 'POST'
      })
        .then(response => response.json())
        .then(jobInfo => {
          if (jobInfo.error) {
            throw new Error(jobInfo.error)
          } else {
            return pollJob(jobInfo);
          }
        })
        .then(response => {
          const newJobId = response.revenue_recognition_report_job.id;
          const search = { job_id: newJobId, school_year: schoolYearForReport };
          const resSearchString = `?${queryString.stringify(search)}`;

          history.push({
            ...location,
            search: resSearchString
          });
        })
        .catch((error) => {
          if (error && error.message) {
            dispatch(modals.actions.show('UnexpectedError', {message: error.message}));
          } else {
            dispatch(modals.actions.show('UnexpectedError'));
          }
          setReady(false);
          setReportLoading(false);
        });
    }
  };

  const forceNewReport = () => {
    getNewReport(true);
  }

  return <React.Fragment>
    <ReportExportButton
      dispatch={dispatch}
      location={location}
      ready={ready}
      reportLoading={reportLoading}
      url='/api/revenue_recognition_report'
    />

    <ReportHeader
      title='Revenue Recognition Report'
    />

    <SchoolYearFilter
      location={location}
      schoolYears={schoolYears}
      schoolYearId={schoolYearId}
      disabled={reportLoading}
    />

    <ReportRunButtons
      runReport={getNewReport}
      forceRunReport={forceNewReport}
      showSingleButton={!ready}
      showBothButtons={ready}
      disableRunButton={!ready || reportLoading || newSchoolYearId === schoolYearId}
      disableRerunButton={!ready || reportLoading}
      showTimestamp={!reportLoading}
      generatedAt={generated_at}
    />

    <Placeholder.invisible ready={ready} content={() => {
      return reportLoading ? null : <ContactList
        columns={columns}
        className='revenue-report-list'
        contacts={reportRows}
        emptyMessage={"There are no billing items for the selected school year."}
        makeRow={(props) => {
          return <ContactRow className={classNames([
            'revenue-recognition-category-row'
          ])} {...props}/>
        }}
      />
    }}/>
  </React.Fragment>
}

const mapDucksToProps = ({location}) => {
  return {
    currentCustomer,
    schoolYears: filterDuck(schoolYearsDuck, { excluding_archived: true }),
  }
}

export default compose(
  withRouter,
  withFiltering,
  duckify(mapDucksToProps)
)(RevenueRecognitionReport)
